import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Grid,
  IconButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { useTheme, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDispatch, useSelector } from "react-redux";
import { fetchLocationBased, fetchLocationBasedEmployees } from "../../actions/LocationBasedAction";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import homeImage from "../../assets/DTS2.png";

// ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "900px",
//   margin: "0 auto",
//   padding: "10px 15px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down('sm')]: {
//     padding: "10px 10px",
//     height: "auto",
//   },
// }));
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down('sm')]: {
    padding: "10px 10px",
  },
}));

const StickyCardsContainer = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "10px",
  paddingBottom: "10px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  [theme.breakpoints.down('sm')]: {
    position: "sticky",
    top: 0,
    backgroundColor: "#ffffff",
  },
}));

// const ScrollableContent = styled(Box)(({ theme }) => ({
//   overflowY: "auto",
//   maxHeight: "calc(100vh - 250px)",
//   [theme.breakpoints.down('sm')]: {
//     overflowY: "auto",
//     maxHeight: "calc(100vh - 280px)",
//   },
// }));
const ScrollableContent = styled(Box)(({ theme }) => ({
  overflowY: "visible",
  [theme.breakpoints.down('sm')]: {
    overflowY: "visible",
  },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  position: "relative",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "10px 14px",
  fontSize: "13px",
  textAlign: "center",
  lineHeight: "1.5",
  whiteSpace: "nowrap",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    fontSize: "13px",
    padding: "12px 14px",
    "&:first-of-type": { borderTopLeftRadius: "16px" },
    "&:last-of-type":  { borderTopRightRadius: "16px" },
  },
  [theme.breakpoints.down('sm')]: {
    padding: "8px 10px",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
}));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "14px 10px",
//   fontSize: "14px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.5px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "16px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "16px",
//     },
//   },
//   [theme.breakpoints.down('sm')]: {
//     padding: "12px 8px",
//     fontSize: "12px",
//     "&.MuiTableCell-head": {
//       position: "sticky",
//       fontSize: "13px",
//     },
//   },
// }));

const SummaryTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "10px 14px",
  fontSize: "13px",
  textAlign: "center",
  lineHeight: "1.5",
  whiteSpace: "nowrap",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    fontSize: "13px",
    padding: "12px 14px",
    "&:first-of-type": { borderTopLeftRadius: "16px" },
    "&:last-of-type":  { borderTopRightRadius: "16px" },
  },
  [theme.breakpoints.down('sm')]: {
    padding: "8px 10px",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
}));

const SummaryTableRow = styled(TableRow)(() => ({
  transition: "0.2s ease",
  "&:nth-of-type(odd)": { backgroundColor: "#f4f7fb" },
  "&:nth-of-type(even)": { backgroundColor: "#ffffff" },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.08)",
    cursor: "pointer",
  },
  "&:last-of-type td:first-of-type": { borderBottomLeftRadius: "16px" },
  "&:last-of-type td:last-of-type":  { borderBottomRightRadius: "16px" },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.09)",
    cursor: "pointer",
  },
  "&:last-of-type td:first-of-type": {
    borderBottomLeftRadius: "16px",
  },
  "&:last-of-type td:last-of-type": {
    borderBottomRightRadius: "16px",
  },
}));
// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.2s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "#f4f7fb",
//   },
//   "&:nth-of-type(even)": {
//     backgroundColor: "#ffffff",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.08)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": { borderBottomLeftRadius: "16px" },
//   "&:last-of-type td:last-of-type":  { borderBottomRightRadius: "16px" },
// }));

const ChartContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#f9fafc",
  borderRadius: "10px",
  padding: theme.spacing(1.5),
  boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
  border: "1px solid #e6e9ef",
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: "#0e3c66",
  marginBottom: theme.spacing(1),
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper
        sx={{
          padding: "8px 10px",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          border: "1px solid #154c79",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: "150px",
        }}
      >
        <Box sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#154c79", textTransform: "uppercase" }}>
            {data.location}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>Total Strength:</Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>{data.total}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>Attendance:</Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>{data.real} ({data.percentage}%)</Typography>
          </Box>
        </Box>
      </Paper>
    );
  }
  return null;
};

// DETAIL VIEW — Employee list for a selected location
function LocationEmployeeDetail({ location, date, employees, empLoading, onBack, formatTime }) {

  const sortedEmployees = useMemo(() => {
  if (!employees || employees.length === 0) return [];
  return [...employees].sort((a, b) => {

    const aHasOut = a.outTime ? 0 : 1;
    const bHasOut = b.outTime ? 0 : 1;
    if (aHasOut !== bHasOut) return aHasOut - bHasOut;
    if (!a.inTime) return 1;
    if (!b.inTime) return -1;
    return a.inTime.localeCompare(b.inTime);
  });
}, [employees]);

  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text("Employee Attendance Details - " + location, pageWidth / 2, 75, { align: "center" });

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    const startX = 15;
    const totalWidth = (pageWidth - 15) - startX;
    const yPosition = 88;
    const section1 = startX + (totalWidth * 0.15);
    const section2 = startX + (totalWidth * 0.45);
    const section3 = startX + (totalWidth * 0.80);

    const dateText = "Date -  " + date;
    const dateWidth = doc.getTextWidth(dateText);
    doc.setFont("helvetica", "bold");
    doc.text("Date -  ", section1 - (dateWidth / 2), yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(date, section1 - (dateWidth / 2) + doc.getTextWidth("Date -  "), yPosition);

    const timeText = "Time -  " + formatTime(generatedAt);
    const timeWidth = doc.getTextWidth(timeText);
    doc.setFont("helvetica", "bold");
    doc.text("Time -  ", section2 - (timeWidth / 2), yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(formatTime(generatedAt), section2 - (timeWidth / 2) + doc.getTextWidth("Time -  "), yPosition);

    const totalText = "Total Employees -    " + sortedEmployees.length.toString();
    const totalTextWidth = doc.getTextWidth(totalText);
    doc.setFont("helvetica", "bold");
    doc.text("Total Employees -   ", section3 - (totalTextWidth / 2), yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(sortedEmployees.length.toString(), section3 - (totalTextWidth / 2) + doc.getTextWidth("Total Employees -    "), yPosition);

    autoTable(doc, {
      startY: 95,
      head: [["Service No", "Name", "Designation", "In Time", "Out Time"]],
      body: sortedEmployees.map((emp) => [
        emp.serviceNo || "",
        emp.name || "",
        emp.designation || "",
        emp.inTime || "",
        emp.outTime || "Available",
      ]),
      styles: { fontSize: 11, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: [21, 76, 121], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 10, halign: "center", cellPadding: 6 },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 30, halign: "center" },
        1: { cellWidth: 50, halign: "left" },
        2: { cellWidth: 45, halign: "left" },
        3: { cellWidth: 30, halign: "center" },
        4: { cellWidth: 30, halign: "center" },
      },
      margin: { left: 13, right: 10 },
      theme: "grid",
      didParseCell: function (data) {
        if (data.section === "body" && data.column.index === 4) {
          if (String(data.cell.raw).trim() === "Available") {
            data.cell.styles.textColor = [0, 181, 26];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = data.row.index % 2 !== 0 ? [255, 255, 255] : [248, 249, 252];
          } else {
            data.cell.styles.textColor = [211, 47, 47];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = data.row.index % 2 !== 0 ? [255, 255, 255] : [248, 249, 252];
          }
        }
      },
      didDrawPage: function (data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont("helvetica", "italic");
        doc.text(`Generated on ${date} at ${formatTime(generatedAt)}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Attendance_${location}_${date}.pdf`);
  };

  return (
    <PageContainer>
      {/* Title with Back Button */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton
          onClick={onBack}
          sx={{
            backgroundColor: "#154C79",
            color: "#fff",
            marginRight: "12px",
            "&:hover": { backgroundColor: "#0e3c66" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#0E3C66",
            letterSpacing: "1px",
            flex: 1,
            textAlign: "center",
            marginRight: "48px",
          }}
        >
          {location} - Employee Details
        </Typography>
      </Box>

      {/* ── Date | Total | Export PDF — single row on desktop ── */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        {/* Date Display */}
        <Grid item xs={6} sm={4}>
          <Box
            sx={{
              background: "#142c54",
              color: "#fff",
              borderRadius: "20px",
              height: "56px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            {date}
          </Box>
        </Grid>

        {/* Total Count */}
        <Grid item xs={6} sm={4}>
          <Box
            sx={{
              background: "#e8f0fa",
              borderRadius: "20px",
              height: "56px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              color: "#0E3C66",
            }}
          >
            Total: {sortedEmployees.length}
          </Box>
        </Grid>

        {/* Export PDF Button */}
        <Grid item xs={12} sm={4}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportPDF}
            disabled={empLoading || !sortedEmployees.length}
            sx={{
              width: "100%",
              height: "56px",
              borderRadius: "20px",
              backgroundColor: "#2e7d32",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: { xs: "13px", sm: "16px" },
              "&:hover": { backgroundColor: "#1b5e20" },
              "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
            }}
          >
            Export PDF
          </Button>
        </Grid>
      </Grid>

      {/* Employee Table */}
      <StyledTableContainer
        component={Paper}
        sx={{ maxHeight: "650px", overflowY: "auto", overflowX: "auto" }}
      >
        <Table sx={{ minWidth: { xs: "100%", sm: 480 } }}>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ textAlign: "center", minWidth: 70 }}>
                Service No
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "left", minWidth: 120 }}>
                Name & Designation
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", minWidth: 70 }}>
                In Time
              </StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center", minWidth: 80 }}>
                Status
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!empLoading && sortedEmployees.length > 0 ? (
              sortedEmployees.map((emp, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell sx={{ textAlign: "center", fontSize: { xs: "12px", sm: "14px" } }}>
                    {emp.serviceNo}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "left" }}>
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: { xs: "12px", sm: "14px" },
                      fontFamily: "'Poppins', sans-serif",
                      color: "#0e3c66",
                      lineHeight: 1.3,
                    }}>
                      {emp.name}
                    </Typography>
                    <Typography sx={{
                      fontSize: { xs: "10px", sm: "12px" },
                      fontFamily: "'Poppins', sans-serif",
                      color: "#666",
                      lineHeight: 1.3,
                    }}>
                      {emp.designation}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center", fontSize: { xs: "12px", sm: "14px" } }}>
                    {emp.inTime}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>
                    {emp.outTime ? (
                      <Box>
                        <Typography sx={{
                          color: "#D32F2F",
                          fontWeight: 600,
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: { xs: "11px", sm: "13px" },
                          lineHeight: 1.3,
                        }}>
                          {emp.outTime}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{
                        color: "#2E7D32",
                        fontWeight: 700,
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: { xs: "11px", sm: "13px" },
                      }}>
                        Available
                      </Typography>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={4}>
                  {empLoading ? "Loading..." : "No Records Found"}
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </PageContainer>
  );
}


// MAIN COMPONENT
export default function LocationBasedEmp() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { loading, data, empLoading, empData } = useSelector((state) => state.locationBased);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDetail, setShowDetail] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");

  const scrollableContentRef = useRef(null);
  const chartContainerRef = useRef(null);

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };
  

  const formatTime = (dateObj) => {
    const d = new Date(dateObj);
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  useEffect(() => {
    dispatch(fetchLocationBased(formatDate(new Date())));
  }, [dispatch]);

  useEffect(() => {
    const scrollableElement = scrollableContentRef.current;
    const chartElement = chartContainerRef.current;
    if (!scrollableElement || !chartElement) return;
    const handleScroll = () => {
      const chartRect = chartElement.getBoundingClientRect();
      const scrollableRect = scrollableElement.getBoundingClientRect();
      const chartFullyVisible = chartRect.bottom <= scrollableRect.bottom + 10;
      if (!chartFullyVisible) {
        const maxScroll = chartElement.offsetTop + chartElement.offsetHeight - scrollableElement.clientHeight;
        if (scrollableElement.scrollTop > maxScroll) {
          scrollableElement.scrollTop = maxScroll;
        }
      }
    };
    scrollableElement.addEventListener('scroll', handleScroll);
    return () => scrollableElement.removeEventListener('scroll', handleScroll);
  }, [loading, data]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    dispatch(fetchLocationBased(formatDate(date)));
  };

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
    setTimeout(() => {
      setShowDetail(true);
      dispatch(fetchLocationBasedEmployees(formatDate(selectedDate), location));
    }, 250);
  };

  const handleBackClick = () => {
    setShowDetail(false);
    setSelectedLocation("");
  };

  const sortedData = data
    ? [...data].sort((a, b) => a.Emp_location.localeCompare(b.Emp_location))
    : [];

  const chartData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return [];
    return sortedData
      .filter((item) => Number(item.Emp_real || 0) > 0)
      .map((item) => ({
        location: item.Emp_location,
        total: Number(item.Emp_Total),
        real: Number(item.Emp_real),
        absent: Number(item.Emp_Total) - Number(item.Emp_real),
        percentage: Math.round(Number(item.Percentage)),
      }))
      .sort((a, b) => a.location.localeCompare(b.location));
  }, [sortedData]);

  const totalStrength = !loading && data?.length > 0 ? data.reduce((sum, item) => sum + Number(item.Emp_Total || 0), 0) : 0;
  const activeCount = !loading && data?.length > 0 ? data.reduce((sum, item) => sum + Number(item.Emp_real || 0), 0) : 0;
  const activePercentage = totalStrength > 0 ? Math.round((activeCount / totalStrength) * 100) : 0;
  const absentCount = totalStrength - activeCount;
  const absentPercentage = totalStrength > 0 ? Math.round((absentCount / totalStrength) * 100) : 0;

  // ← these four MUST come after activeCount
  const totalOut = !loading && data?.length > 0 ? data.reduce((sum, item) => sum + Number(item.Emp_Out || 0), 0) : 0;
  const totalAvailable = !loading && data?.length > 0 ? data.reduce((sum, item) => sum + Number(item.Emp_Available || 0), 0) : 0;
  const totalOutPercentage = activeCount > 0 ? Math.round((totalOut / activeCount) * 100) : 0;
  const totalAvailablePercentage = activeCount > 0 ? Math.round((totalAvailable / activeCount) * 100) : 0;

  // PDF EXPORT FOR TABLE
  const handleExportTablePDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text("Employee Strength & Attendance Report", pageWidth / 2, 75, { align: "center" });
    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    const startX = 15;
    const totalWidth = (pageWidth - 15) - startX;
    const yPosition = 88;
    const section1 = startX + (totalWidth * 0.125);
    const section2 = startX + (totalWidth * 0.375);
    const section3 = startX + (totalWidth * 0.625);
    const section4 = startX + (totalWidth * 0.875);
    const dateText = "Date - " + formatDate(selectedDate);
    const dateWidth = doc.getTextWidth(dateText);
    doc.setFont("helvetica", "bold");
    doc.text("Date - ", section1 - (dateWidth / 2), yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(selectedDate), section1 - (dateWidth / 2) + doc.getTextWidth("Date - "), yPosition);
    const timeText = "Time - " + formatTime(generatedAt);
    const timeWidth = doc.getTextWidth(timeText);
    doc.setFont("helvetica", "bold");
    doc.text("Time - ", section2 - (timeWidth / 2), yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(formatTime(generatedAt), section2 - (timeWidth / 2) + doc.getTextWidth("Time - "), yPosition);
    const strengthText = "Total Strength - " + totalStrength.toString();
    const strengthWidth = doc.getTextWidth(strengthText);
    doc.setFont("helvetica", "bold");
    doc.text("Total Strength - ", section3 - (strengthWidth / 2), yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(totalStrength.toString(), section3 - (strengthWidth / 2) + doc.getTextWidth("Total Strength -   "), yPosition);
    const attendanceText = "Attendance - " + `${activeCount} (${activePercentage}%)`;
    const attendanceWidth = doc.getTextWidth(attendanceText);
    doc.setFont("helvetica", "bold");
    doc.text("Attendance - ", section4 - (attendanceWidth / 2), yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(`${activeCount} (${activePercentage}%)`, section4 - (attendanceWidth / 2) + doc.getTextWidth("Attendance -   "), yPosition);
    const columns = [
      "Location",
      "Total",
      "Attendance",
      "Att %",
      "Out Count",
      "Balance",
    ];

    const rows = sortedData?.map(item => [
      item.Emp_location || "",
      item.Emp_Total || "",
      item.Emp_real || "",
      `${Math.round(Number(item.Percentage))}%`,
      item.Emp_Out || "0",
      item.Emp_Available || "0",
    ]);

    rows.push([
      "Total",
      totalStrength.toString(),
      activeCount.toString(),
      `${activePercentage}%`,
      totalOut.toString(),
      totalAvailable.toString(),
    ]);

    autoTable(doc, {
      startY: 95,
      head: [columns],
      body: rows,
      styles: {
        fontSize: 10,
        cellPadding: 5,
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [21, 76, 121],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 10,
        halign: "center",
        cellPadding: 6,
        valign: "middle",
      },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 32 },
        2: { cellWidth: 28 },
        3: { cellWidth: 28 },
        4: { cellWidth: 34 },
        5: { cellWidth: 34 },
      },
      margin: { left: 14, right: 14 },
      theme: "grid",
      didParseCell: function (data) {
        const isTotalRow = data.row.index === rows.length - 1;

        if (isTotalRow) {
          data.cell.styles.fillColor = [21, 76, 121];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          if (data.column.index === 4 || data.column.index === 5) {
            data.cell.styles.minCellHeight = 18;
          }
        }
      },
      didDrawCell: function (data) {
        const isTotalRow = data.row.index === rows.length - 1;


        if (isTotalRow && data.section === "body") {
          if (data.column.index === 4) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(255, 205, 210);
            const x = data.cell.x + data.cell.width / 2;
            const y = data.cell.y + data.cell.height - 4;
            doc.text(`(${totalOutPercentage}%)`, x, y, { align: "center" });
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
          }

          if (data.column.index === 5) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(185, 246, 202);
            const x = data.cell.x + data.cell.width / 2;
            const y = data.cell.y + data.cell.height - 4;
            doc.text(`(${totalAvailablePercentage}%)`, x, y, { align: "center" });
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
          }
        }
      },
      didDrawPage: function (data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });
    doc.save(`Attendance_Table_${formatDate(selectedDate)}.pdf`);
  };

  const handleExportChartPDF = async () => {
    if (!chartContainerRef.current) return;
    try {
      const canvas = await html2canvas(chartContainerRef.current, { scale: 2, backgroundColor: "#f9fafc", logging: false, useCORS: true });
      const doc = new jsPDF("p", "mm", "a4");
      const generatedAt = new Date();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.addImage(homeImage, "PNG", 0, 0, 210, 65);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(21, 76, 121);
      doc.text("Employee Strength & Attendance - Chart View", pageWidth / 2, 75, { align: "center" });
      doc.setDrawColor(21, 76, 121);
      doc.setLineWidth(0.5);
      doc.line(15, 82, pageWidth - 15, 82);
      const imgData = canvas.toDataURL("image/png");
      const chartStartY = 95;
      const availableHeight = pageHeight - chartStartY - 15;
      const chartMargin = 10;
      const chartWidth = pageWidth - (chartMargin * 2);
      let chartHeight = chartWidth / (canvas.width / canvas.height);
      if (chartHeight > availableHeight) chartHeight = availableHeight;
      doc.addImage(imgData, "PNG", (pageWidth - chartWidth) / 2, chartStartY, chartWidth, chartHeight);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.setFont("helvetica", "italic");
      doc.text(`Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      doc.text("Page 1", pageWidth - 20, pageHeight - 10);
      doc.save(`Attendance_Chart_${formatDate(selectedDate)}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Show Detail View
  if (showDetail) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <LocationEmployeeDetail
          location={selectedLocation}
          date={formatDate(selectedDate)}
          employees={empData || []}
          empLoading={empLoading}
          onBack={handleBackClick}
          formatTime={formatTime}
        />
      </LocalizationProvider>
    );
  }

  //Show Summary View
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 2,
          }}
        >
          Employee Strength & Attendance
        </Typography>

        {/* ── Date | Chart PDF | Table PDF — single row on desktop ── */}
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          {/* Date button — full width mobile, half desktop */}
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon />}
              onClick={() => setOpenDatePicker(true)}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#142c54",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "16px",
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
            >
              {formatDate(selectedDate)}
            </Button>
          </Grid>

          {/* Chart PDF — full width mobile, quarter desktop */}
          <Grid item xs={6} sm={3}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportChartPDF}
              disabled={loading || !data?.length}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#1976d2",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: { xs: "13px", sm: "16px" },
                "&:hover": { backgroundColor: "#115293" },
              }}
            >
              Chart PDF
            </Button>
          </Grid>

          {/* Table PDF — full width mobile, quarter desktop */}
          <Grid item xs={6} sm={3}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportTablePDF}
              disabled={loading || !data?.length}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#2e7d32",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: { xs: "13px", sm: "16px" },
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
            >
              Table PDF
            </Button>
          </Grid>
        </Grid>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => (
              <TextField {...params} sx={{ display: "none" }} />
            )}
          />
        )}

        {/* Sticky Cards Section */}
        {!loading && data?.length > 0 && (
          <StickyCardsContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: isMobile ? "10px" : "15px",
                px: isMobile ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  background: "#e8f0fa",
                  border: "1px solid #bbd0ea",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#0E3C66",
                  flex: 1,
                  maxWidth: isMobile ? "48%" : "200px",
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, mb: 0.5 }}>
                  Total Strength
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "20px" : "24px", fontWeight: 700 }}>
                  {totalStrength}
                </Typography>
              </Box>

              <Box
                sx={{
                  background: "linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%)",
                  border: "1px solid #80CBC4",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#004D40",
                  flex: 1,
                  maxWidth: isMobile ? "48%" : "200px",
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, mb: 0.5 }}>
                  Attendance
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "20px" : "24px", fontWeight: 700 }}>
                  {activeCount}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, color: "#2E7D32" }}>
                  ({activePercentage}%)
                </Typography>
              </Box>
            </Box>
          </StickyCardsContainer>
        )}

        {/* Scrollable Content - Chart and Table */}
        <ScrollableContent ref={scrollableContentRef}>
          {/* Chart */}
          {chartData.length > 0 && (
            <ChartContainer ref={chartContainerRef}>
              <ChartTitle>
                Attendance by Location ({chartData.length} locations with attendance)
              </ChartTitle>

              <Box sx={{ width: "100%", height: chartData.length * (isMobile ? 18 : 20) + 60, maxHeight: isMobile ? 450 : 420, overflowY: "auto" }}>
                <ResponsiveContainer width="100%" height={chartData.length * (isMobile ? 18 : 20) + 60}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    barCategoryGap={1}
                    barGap={0}
                    barSize={isMobile ? 12 : 14}
                    margin={{ top: 5, right: isMobile ? 50 : 80, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e6e6e6" horizontal vertical={false} />
                    <YAxis dataKey="location" type="category" axisLine={false} tickLine={false} interval={0} width={isMobile ? 50 : 70} tick={{ fontSize: isMobile ? 9 : 10, fontWeight: 500, fill: "#333" }} />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: isMobile ? 9 : 10 }} domain={[0, "dataMax + 2"]} tickCount={5} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.02)" }} />
                    <Bar dataKey="real" name="Attendance" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`attendance-cell-${index}`} fill={entry.percentage > 70 ? "#4CAF50" : entry.percentage > 50 ? "#FF9800" : "#F44336"} />
                      ))}
                      <LabelList dataKey={(entry) => `${entry.real}/${entry.total} (${entry.percentage}%)`} position="right" offset={8} style={{ fontSize: isMobile ? 9 : 10, fontWeight: 600, fill: "#555" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Color Legend */}
              <Box sx={{ display: "flex", justifyContent: "center", gap: "15px", mt: 1, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box sx={{ width: "14px", height: "14px", backgroundColor: "#4CAF50", borderRadius: "3px" }} />
                  <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>High Attendance (&gt;70%)</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box sx={{ width: "14px", height: "14px", backgroundColor: "#FF9800", borderRadius: "3px" }} />
                  <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>Medium (50-70%)</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box sx={{ width: "14px", height: "14px", backgroundColor: "#F44336", borderRadius: "3px" }} />
                  <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>Low (&lt;50%)</Typography>
                </Box>
              </Box>
            </ChartContainer>
          )}

          {/* TABLE */}
            <StyledTableContainer component={Paper} sx={{ maxHeight: "none", overflow: "visible" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <SummaryTableCell>Loc</SummaryTableCell>
                  <SummaryTableCell>Total</SummaryTableCell>
                  <SummaryTableCell>Att</SummaryTableCell>
                  <SummaryTableCell>%</SummaryTableCell>
                  <SummaryTableCell>Out</SummaryTableCell>
                  <SummaryTableCell>Bal</SummaryTableCell>
                  
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  sortedData.map((row, index) => (
                    <SummaryTableRow
                      key={index}
                      onClick={() => {
                        setTimeout(() => {
                          handleLocationClick(row.Emp_location);
                        }, 250);
                      }}
                    >
                      <SummaryTableCell>{row.Emp_location}</SummaryTableCell>
                      <SummaryTableCell>{row.Emp_Total}</SummaryTableCell>
                      <SummaryTableCell>{row.Emp_real}</SummaryTableCell>
                      <SummaryTableCell>{Math.round(Number(row.Percentage))}%</SummaryTableCell>
                      <SummaryTableCell>{row.Emp_Out}</SummaryTableCell>
                      <SummaryTableCell>{row.Emp_Available}</SummaryTableCell>
                      {/* <SummaryTableCell>{Math.round(Number(row.Out_Percentage))}%</SummaryTableCell>
                      <SummaryTableCell>{Math.round(Number(row.Available_Percentage))}%</SummaryTableCell> */}

                      {/* <SummaryTableCell>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                            {row.Emp_Out}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", color: "#D32F2F", fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                            ({Math.round(Number(row.Out_Percentage))}%)
                          </Typography>
                        </Box>
                      </SummaryTableCell> */}
                      {/* <SummaryTableCell>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                            {row.Emp_Available}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", color: "#2E7D32", fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                            ({Math.round(Number(row.Available_Percentage))}%)
                          </Typography>
                        </Box>
                      </SummaryTableCell> */}
                      
                    </SummaryTableRow>
                  ))}
                {!loading && data?.length > 0 && (
                <SummaryTableRow sx={{ 
                  backgroundColor: "#154c79 !important", 
                  "&:hover": { backgroundColor: "#154c79 !important" },
                  height: "52px",
                }}>
                  <SummaryTableCell sx={{ color: "#ffffff", fontWeight: 700, verticalAlign: "top", paddingTop: "10px" }}>
                    Total
                  </SummaryTableCell>
                  <SummaryTableCell sx={{ color: "#ffffff", fontWeight: 700, verticalAlign: "top", paddingTop: "10px" }}>
                    {totalStrength}
                  </SummaryTableCell>
                  <SummaryTableCell sx={{ color: "#ffffff", fontWeight: 700, verticalAlign: "top", paddingTop: "10px" }}>
                    {activeCount}
                  </SummaryTableCell>
                  <SummaryTableCell sx={{ color: "#ffffff", fontWeight: 700, verticalAlign: "top", paddingTop: "10px" }}>
                    {activePercentage}%
                  </SummaryTableCell>
                  <SummaryTableCell sx={{ verticalAlign: "top", padding: "10px 14px 6px 14px" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", fontFamily: "'Poppins', sans-serif", color: "#ffffff", lineHeight: 1 }}>
                      {totalOut}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#ffcdd2", fontWeight: 500, fontFamily: "'Poppins', sans-serif", lineHeight: 1.4, mt: "2px" }}>
                      ({totalOutPercentage}%)
                    </Typography>
                  </SummaryTableCell>
                  <SummaryTableCell sx={{ verticalAlign: "top", padding: "10px 14px 6px 14px" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", fontFamily: "'Poppins', sans-serif", color: "#ffffff", lineHeight: 1 }}>
                      {totalAvailable}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#b9f6ca", fontWeight: 500, fontFamily: "'Poppins', sans-serif", lineHeight: 1.4, mt: "2px" }}>
                      ({totalAvailablePercentage}%)
                    </Typography>
                  </SummaryTableCell>
                </SummaryTableRow>
                )}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Loading...</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </ScrollableContent>
      </PageContainer>
    </LocalizationProvider>
  );
}